// Note entry and note/beat edits (§1.2, §1.3, §4.1, §4.2, §5.3).

import type { Draft } from 'immer';
import {
  type Step,
  type Alter,
  type DurBase,
  type Duration,
  type Pitch,
  midiOf,
  nearestPitchForLetter,
  diatonicInterval,
  spellMidi,
  tabToMidi,
  toggleEnharmonic as toggleEnh,
  shorter as shorterDur,
  longer as longerDur,
  staffPosition,
  pitchAtStaffPosition,
  diatonicStep,
  fretOn,
  ticks,
  barCapacity,
  durationForTicks,
} from '@simplenotation/theory';
import type { Score, Beat, Note, BeatRef, Articulation, Dynamic, NoteHead, SlideType, ChordSymbol } from '../model/types';
import { createNote, createRest, reId } from '../model/factories';
import { voiceOf, previousPitch, topNote, bottomNote, isFretted, soundingMidi, timeSigAt, filledTicks } from '../model/query';
import {
  type EditorState,
  type Lane,
  mutate,
  withError,
  trackKeyAt,
  middleLineAt,
  staffPosOf,
  draftVoice,
  draftBeat,
  refingerBeat,
  retabKeepStrings,
  selectedRefs,
  targetNotes,
  hasRange,
  focusBeat,
  samePitch,
  cursorNoteIndexes,
} from './state';
import { moveRight, appendBarDraft } from './navigation';

export const MAX_FRET = 30;

/** If the cursor sits on an insertion point (empty voice), create a rest beat there. */
function ensureBeat(st: EditorState): EditorState {
  const c = st.cursor;
  const beats = voiceOf(st.score, c);
  if (c.beat < beats.length) return st;
  return mutate(st, (d) => {
    const v = draftVoice(d, c);
    v.push(createRest({ ...st.duration }));
  });
}

/** A–G on the staff: set the beat's pitch (or append with Shift) at the nearest octave. */
export function enterLetter(st0: EditorState, step: Step, append = false): EditorState {
  const st = ensureBeat(st0);
  const c = st.cursor;
  const beat = voiceOf(st.score, c)[c.beat];
  const key = trackKeyAt(st.score, c.track, c.bar);
  const mid = middleLineAt(st.score, c.track, c.bar);
  let pitch: Pitch;
  if (append && !beat.rest && beat.notes.length) {
    pitch = nearestPitchForLetter(step, topNote(beat).pitch, key.fifths, mid);
    if (beat.notes.some((n) => samePitch(n.pitch, pitch))) return st;
  } else {
    pitch = nearestPitchForLetter(step, previousPitch(st.score, c), key.fifths, mid);
  }
  const next = mutate(st, (d) => {
    const b = draftBeat(d, c)!;
    if (append && !b.rest && b.notes.length) {
      b.notes.push(createNote(pitch));
    } else {
      b.notes = [createNote(pitch)];
      b.rest = false;
    }
    refingerBeat(d, c);
  });
  return afterPitchEntry(next, pitch);
}

function afterPitchEntry(st: EditorState, pitch: Pitch): EditorState {
  const c = st.cursor;
  const beat = voiceOf(st.score, c)[c.beat];
  const note = beat?.notes.find((n) => samePitch(n.pitch, pitch));
  return {
    ...st,
    cursor: {
      ...c,
      staffPos: staffPosOf(st.score, c, pitch),
      string: note?.string ?? c.string,
    },
  };
}

/** 2–8 (Shift: below): add a diatonic interval above the top note / below the bottom note. */
export function appendInterval(st: EditorState, n: number, dir: 1 | -1 = 1): EditorState {
  const c = st.cursor;
  const beat = voiceOf(st.score, c)[c.beat];
  if (!beat || beat.rest || beat.notes.length === 0) return withError(st, 'Enter a note first, then add an interval.');
  const key = trackKeyAt(st.score, c.track, c.bar);
  const base = dir > 0 ? topNote(beat).pitch : bottomNote(beat).pitch;
  const pitch = diatonicInterval(base, n, dir, key.fifths);
  if (beat.notes.some((x) => samePitch(x.pitch, pitch))) return st;
  const next = mutate(st, (d) => {
    draftBeat(d, c)!.notes.push(createNote(pitch));
    refingerBeat(d, c);
  });
  return afterPitchEntry(next, pitch);
}

/** Set a pitch directly (MIDI input, touch fretboard/piano, staff click entry). */
export function enterPitch(st0: EditorState, pitch: Pitch, append = false, pos?: { string: number; fret: number }): EditorState {
  const st = ensureBeat(st0);
  const c = st.cursor;
  const next = mutate(st, (d) => {
    const b = draftBeat(d, c)!;
    const note = createNote(pitch, pos ? { string: pos.string, fret: pos.fret } : {});
    if (append && !b.rest && b.notes.length) {
      if (b.notes.some((n) => samePitch(n.pitch as Pitch, pitch))) return;
      if (pos) {
        const i = b.notes.findIndex((n) => n.string === pos.string);
        if (i >= 0) b.notes.splice(i, 1);
      }
      b.notes.push(note);
    } else {
      b.notes = [note];
      b.rest = false;
    }
    if (!pos) refingerBeat(d, c);
  });
  return afterPitchEntry(next, pitch);
}

/** Enter a sounding MIDI number (MIDI keyboard / piano): spelled in the key. */
export function enterMidi(st: EditorState, soundingMidi: number, append = false): EditorState {
  const c = st.cursor;
  const track = st.score.tracks[c.track];
  const key = trackKeyAt(st.score, c.track, c.bar);
  return enterPitch(st, spellMidi(soundingMidi - track.writtenToSounding, key.fifths), append);
}

/** Digits on tab: set the fret of the note on the cursor string. */
export function setFret(st0: EditorState, fret: number): EditorState {
  const c0 = st0.cursor;
  const track = st0.score.tracks[c0.track];
  if (!isFretted(track)) return withError(st0, 'This instrument has no tablature.');
  if (fret < 0 || fret > MAX_FRET) return withError(st0, `Fret ${fret} is out of range.`);
  const strings = track.tuning!.length;
  if (c0.string < 1 || c0.string > strings) return withError(st0, 'No string under the cursor.');
  const st = ensureBeat(st0);
  const c = st.cursor;
  const key = trackKeyAt(st.score, c.track, c.bar);
  const sounding = tabToMidi(c.string, fret, track.tuning!, track.capo ?? 0);
  const pitch = spellMidi(sounding - track.writtenToSounding, key.fifths);
  const next = mutate(st, (d) => {
    const b = draftBeat(d, c)!;
    const existing = b.rest ? undefined : b.notes.find((n) => n.string === c.string);
    if (existing) {
      // Keep the spelling if the pitch did not change (e.g. retyping the same fret).
      if (midiOf(existing.pitch) !== midiOf(pitch)) existing.pitch = pitch;
      existing.fret = fret;
    } else if (b.rest || b.notes.length === 0) {
      b.notes = [createNote(pitch, { string: c.string, fret })];
      b.rest = false;
    } else {
      b.notes.push(createNote(pitch, { string: c.string, fret }));
    }
  });
  return { ...next, cursor: { ...next.cursor, staffPos: staffPosOf(next.score, c, pitch) } };
}

/** R: turn the selected beats into rests (or create a rest at an insertion point). */
export function toggleRest(st0: EditorState): EditorState {
  const st = ensureBeat(st0);
  const refs = selectedRefs(st);
  const allRests = refs.every((r) => voiceOf(st.score, r)[r.beat]?.rest);
  if (allRests) return st;
  return mutate(st, (d) => {
    for (const r of refs) {
      const b = draftBeat(d, r);
      if (!b) continue;
      b.rest = true;
      b.notes = [];
    }
  });
}

/**
 * Backspace/Delete: remove the cursor note from a chord, turn a note beat into
 * a rest, or remove a rest beat. A range selection is cleared to rests.
 */
export function deleteAtCursor(st: EditorState): EditorState {
  const c = st.cursor;
  if (hasRange(st)) {
    return mutate(st, (d) => {
      for (const r of selectedRefs(st)) {
        const b = draftBeat(d, r);
        if (b) {
          b.rest = true;
          b.notes = [];
        }
      }
    });
  }
  const beats = voiceOf(st.score, c);
  const beat = beats[c.beat];
  if (!beat) return st;
  if (!beat.rest && beat.notes.length > 1) {
    const idx = cursorNoteIndexes(st, beat);
    if (idx.length === 1) {
      return mutate(st, (d) => {
        draftBeat(d, c)!.notes.splice(idx[0], 1);
      });
    }
  }
  if (!beat.rest) {
    return mutate(st, (d) => {
      const b = draftBeat(d, c)!;
      b.rest = true;
      b.notes = [];
    });
  }
  // Rest: remove the beat (voice 0 keeps at least one beat per bar).
  if (c.voice === 0 && beats.length <= 1) return st;
  const next = mutate(st, (d) => {
    draftVoice(d, c).splice(c.beat, 1);
  });
  const remaining = voiceOf(next.score, c).length;
  return focusBeat(next, { ...c, beat: Math.max(0, Math.min(c.beat, remaining - 1)) });
}

/** Apply a duration change to the selected beats (or the input duration only on an insertion point). */
function changeDurations(st: EditorState, f: (d: Duration) => Duration): EditorState {
  const refs = selectedRefs(st);
  const newInput = f(st.duration);
  if (refs.length === 0) return { ...st, duration: newInput };
  const next = mutate(st, (d) => {
    for (const r of refs) {
      const b = draftBeat(d, r);
      if (b) b.duration = f({ ...(b.duration as Duration) });
    }
  });
  return { ...next, duration: newInput };
}

export function setDurationBase(st: EditorState, base: DurBase): EditorState {
  return changeDurations(st, (d) => ({ ...d, base }));
}

/** "+": shorter (quarter → eighth). */
export function shorter(st: EditorState): EditorState {
  return changeDurations(st, (d) => shorterDur(d));
}

/** "-": longer (quarter → half). */
export function longer(st: EditorState): EditorState {
  return changeDurations(st, (d) => longerDur(d));
}

/** ".": toggle an augmentation dot. */
export function toggleDot(st: EditorState): EditorState {
  const cur = voiceOf(st.score, st.cursor)[st.cursor.beat];
  const on = (cur?.duration.dots ?? st.duration.dots) === 0;
  return changeDurations(st, (d) => ({ ...d, dots: on ? 1 : 0 }));
}

export function setDots(st: EditorState, dots: 0 | 1 | 2): EditorState {
  return changeDurations(st, (d) => ({ ...d, dots }));
}

/** "/": toggle a tuplet (default triplet 3:2) on the selected beats and the input duration. */
export function toggleTuplet(st: EditorState, actual = 3, normal = 2): EditorState {
  const cur = voiceOf(st.score, st.cursor)[st.cursor.beat];
  const on = !(cur?.duration.tuplet ?? st.duration.tuplet);
  return changeDurations(st, (d) => {
    const n: Duration = { base: d.base, dots: d.dots };
    if (on) n.tuplet = { actual, normal };
    return n;
  });
}

/** Mod+G/H/J: flat / natural / sharp on the targeted notes. */
export function setAccidental(st: EditorState, alter: Alter): EditorState {
  const targets = targetNotes(st);
  if (!targets.length) return st;
  return mutate(st, (d) => {
    for (const { ref, idx } of targets) {
      const b = draftBeat(d, ref)!;
      for (const i of idx) {
        const n = b.notes[i];
        n.pitch = { ...n.pitch, alter } as Pitch;
      }
      retabKeepStrings(d, ref);
    }
  });
}

/** Raise/lower by a chromatic semitone keeping the letter when possible (double accidentals allowed). */
export function nudgeAccidental(st: EditorState, delta: 1 | -1): EditorState {
  const targets = targetNotes(st);
  if (!targets.length) return st;
  return mutate(st, (d) => {
    for (const { ref, idx } of targets) {
      const b = draftBeat(d, ref)!;
      for (const i of idx) {
        const n = b.notes[i];
        const a = n.pitch.alter + delta;
        if (a >= -2 && a <= 2) n.pitch = { ...n.pitch, alter: a as Alter };
      }
      retabKeepStrings(d, ref);
    }
  });
}

/** Mod+E: toggle the enharmonic spelling. */
export function toggleEnharmonic(st: EditorState): EditorState {
  const targets = targetNotes(st);
  if (!targets.length) return st;
  const next = mutate(st, (d) => {
    for (const { ref, idx } of targets) {
      const b = draftBeat(d, ref)!;
      for (const i of idx) b.notes[i].pitch = toggleEnh(b.notes[i].pitch as Pitch);
    }
  });
  const t = targets[0];
  const b = voiceOf(next.score, t.ref)[t.ref.beat];
  const n = b?.notes[t.idx[0]];
  if (!n || hasRange(st)) return next;
  return { ...next, cursor: { ...next.cursor, staffPos: staffPosOf(next.score, next.cursor, n.pitch) } };
}

/** Mod+↑/↓: move the targeted notes by an octave. */
export function shiftOctave(st: EditorState, dir: 1 | -1): EditorState {
  const targets = targetNotes(st);
  if (!targets.length) return st;
  const next = mutate(st, (d) => {
    for (const { ref, idx } of targets) {
      const b = draftBeat(d, ref)!;
      for (const i of idx) b.notes[i].pitch = { ...b.notes[i].pitch, octave: b.notes[i].pitch.octave + dir };
      refingerBeat(d, ref);
    }
  });
  return hasRange(st) ? next : { ...next, cursor: { ...next.cursor, staffPos: next.cursor.staffPos + 7 * dir } };
}

/** ↑/↓ on a staff note with Alt? No — diatonic step of the targeted notes (used by drag / Alt+Shift arrows). */
export function stepPitch(st: EditorState, dir: 1 | -1): EditorState {
  const targets = targetNotes(st);
  if (!targets.length) return st;
  const key = trackKeyAt(st.score, st.cursor.track, st.cursor.bar);
  const next = mutate(st, (d) => {
    for (const { ref, idx } of targets) {
      const b = draftBeat(d, ref)!;
      for (const i of idx) {
        const p = b.notes[i].pitch as Pitch;
        b.notes[i].pitch = diatonicInterval(p, 2, dir, key.fifths);
      }
      refingerBeat(d, ref);
    }
  });
  return hasRange(st) ? next : { ...next, cursor: { ...next.cursor, staffPos: next.cursor.staffPos + dir } };
}

/** Alt+↑/↓: move the cursor note to the adjacent string keeping its pitch. `dir` = −1 toward string 1. */
export function moveToString(st: EditorState, dir: 1 | -1): EditorState {
  const c = st.cursor;
  const track = st.score.tracks[c.track];
  if (!isFretted(track)) return st;
  const beat = voiceOf(st.score, c)[c.beat];
  if (!beat || beat.rest) return st;
  const idx = cursorNoteIndexes(st, beat);
  if (idx.length !== 1) return withError(st, 'Put the cursor on a single note to move it to another string.');
  const note = beat.notes[idx[0]];
  if (note.string === undefined) return st;
  const target = note.string + dir;
  if (target < 1 || target > track.tuning!.length) return withError(st, 'No string in that direction.');
  const fret = fretOn(soundingMidi(note, track), target, { tuning: track.tuning!, capo: track.capo ?? 0, maxFret: MAX_FRET });
  if (fret === null) return withError(st, 'That note can’t be played on the next string.');
  if (beat.notes.some((n, i) => i !== idx[0] && n.string === target)) return withError(st, 'Another note already uses that string.');
  const next = mutate(st, (d) => {
    const n = draftBeat(d, c)!.notes[idx[0]];
    n.string = target;
    n.fret = fret;
  });
  return { ...next, cursor: { ...next.cursor, string: target } };
}

/** Where a note dropped with the mouse lands. `ref.beat` may be the voice length (the bar's insertion point). */
export interface PlaceTarget {
  ref: BeatRef;
  lane: Lane;
  string?: number;
  staffPos?: number;
}

/**
 * Palette drag and drop: put a note (or rest) of the given note value on a beat
 * or at the bar's insertion point. On tab the note goes on the dropped string
 * as an open string (type digits next to set the fret); a string that already
 * has a note keeps it. On the staff the note takes the pitch of the line or
 * space in the key. The beat takes the dropped note value. Past the end of a
 * full last bar the score grows by a bar and the note starts it.
 */
export function placeNote(st0: EditorState, target: PlaceTarget, opts: { base?: DurBase; rest?: boolean } = {}): EditorState {
  const { ref, lane } = target;
  const track = st0.score.tracks[ref.track];
  if (!track) return st0;
  if (lane === 'tab' && !opts.rest && !isFretted(track)) return withError(st0, 'This instrument has no tablature.');
  const beats = voiceOf(st0.score, ref);
  const beat = Math.min(ref.beat, beats.length);
  if (beat === beats.length && beats.length > 0 && filledTicks(beats) >= barCapacity(timeSigAt(st0.score, ref.bar))) {
    if (ref.bar === st0.score.bars.length - 1) {
      const grown = mutate(st0, (d) => appendBarDraft(d as Score));
      return placeNote(grown, { ...target, ref: { ...ref, bar: ref.bar + 1, beat: 0 } }, opts);
    }
    return withError(st0, 'This bar is full. Drop the note on a beat to replace it.');
  }
  let st = focusBeat({ ...st0, cursor: { ...st0.cursor, lane } }, { ...ref, beat });
  st = { ...st, cursor: { ...st.cursor, string: target.string ?? st.cursor.string, staffPos: target.staffPos ?? st.cursor.staffPos } };
  if (opts.base) st = setDurationBase(st, opts.base);
  if (beat === beats.length) {
    // A new beat gets at most the time left in the bar (like → past the last beat).
    const left = barCapacity(timeSigAt(st.score, ref.bar)) - filledTicks(beats);
    const fit = ticks(st.duration) > left && left > 0 ? durationForTicks(left) : null;
    if (fit) st = { ...st, duration: fit };
  }
  if (opts.rest) return toggleRest(st);
  const cur = voiceOf(st.score, st.cursor)[st.cursor.beat];
  const hasNotes = !!cur && !cur.rest && cur.notes.length > 0;
  if (lane === 'tab') {
    if (hasNotes && cur.notes.some((n) => n.string === st.cursor.string)) return ensureBeat(st);
    return setFret(st, 0);
  }
  const key = trackKeyAt(st.score, ref.track, ref.bar);
  const pitch = pitchAtStaffPosition(st.cursor.staffPos, middleLineAt(st.score, ref.track, ref.bar), key.fifths);
  return enterPitch(st, pitch, hasNotes);
}

/**
 * Mouse drag on tab: move the cursor note to another string. By default the
 * fret number stays and the pitch follows the string; with `keepPitch` the
 * note is re-fretted at the same pitch (like Alt+↑/↓). Unplayable or occupied
 * strings leave the note where it is.
 */
export function dragNoteToString(st: EditorState, target: number, keepPitch = false): EditorState {
  const c = st.cursor;
  const track = st.score.tracks[c.track];
  if (!isFretted(track) || target === c.string || target < 1 || target > track.tuning!.length) return st;
  const beat = voiceOf(st.score, c)[c.beat];
  if (!beat || beat.rest) return st;
  const i = beat.notes.findIndex((n) => n.string === c.string);
  if (i < 0 || beat.notes.some((n, j) => j !== i && n.string === target)) return st;
  const note = beat.notes[i];
  let fret: number;
  let pitch = note.pitch;
  if (keepPitch) {
    const f = fretOn(soundingMidi(note, track), target, { tuning: track.tuning!, capo: track.capo ?? 0, maxFret: MAX_FRET });
    if (f === null) return st;
    fret = f;
  } else {
    fret = note.fret ?? 0;
    const key = trackKeyAt(st.score, c.track, c.bar);
    pitch = spellMidi(tabToMidi(target, fret, track.tuning!, track.capo ?? 0) - track.writtenToSounding, key.fifths);
  }
  const next = mutate(st, (d) => {
    const n = draftBeat(d, c)!.notes[i];
    n.string = target;
    n.fret = fret;
    n.pitch = pitch;
  });
  return { ...next, cursor: { ...next.cursor, string: target, staffPos: staffPosOf(next.score, c, pitch) } };
}

/** Mouse drag on the staff: move the cursor note to another line or space (pitch in the key). */
export function dragNoteToStaffPos(st: EditorState, target: number): EditorState {
  const c = st.cursor;
  if (target === c.staffPos) return st;
  const beat = voiceOf(st.score, c)[c.beat];
  if (!beat || beat.rest) return st;
  const mid = middleLineAt(st.score, c.track, c.bar);
  const i = beat.notes.findIndex((n) => staffPosition(n.pitch, mid) === c.staffPos);
  if (i < 0 || beat.notes.some((n, j) => j !== i && staffPosition(n.pitch, mid) === target)) return st;
  const key = trackKeyAt(st.score, c.track, c.bar);
  const pitch = diatonicStep(beat.notes[i].pitch, target - c.staffPos, key.fifths);
  const next = mutate(st, (d) => {
    draftBeat(d, c)!.notes[i].pitch = pitch;
    refingerBeat(d, c);
  });
  const moved = voiceOf(next.score, c)[c.beat].notes[i];
  return { ...next, cursor: { ...next.cursor, staffPos: target, string: moved?.string ?? next.cursor.string } };
}

/** Following beat ref in the voice (across bars), or null. */
function nextRef(score: Score, ref: BeatRef): BeatRef | null {
  const beats = voiceOf(score, ref);
  if (ref.beat + 1 < beats.length) return { ...ref, beat: ref.beat + 1 };
  for (let bar = ref.bar + 1; bar < score.bars.length; bar++) {
    if (voiceOf(score, { ...ref, bar }).length) return { ...ref, bar, beat: 0 };
  }
  return null;
}

function prevRef(score: Score, ref: BeatRef): BeatRef | null {
  if (ref.beat > 0) return { ...ref, beat: ref.beat - 1 };
  for (let bar = ref.bar - 1; bar >= 0; bar--) {
    const n = voiceOf(score, { ...ref, bar }).length;
    if (n) return { ...ref, bar, beat: n - 1 };
  }
  return null;
}

/** Tie to next: toggle ties; when turning on, the next beat gets the tied pitches (created if needed). */
export function tieToNext(st0: EditorState): EditorState {
  const c = st0.cursor;
  const beat = voiceOf(st0.score, c)[c.beat];
  if (!beat || beat.rest || !beat.notes.length) return withError(st0, 'Nothing to tie.');
  const on = !beat.notes.every((n) => n.tieToNext);
  if (!on) {
    return mutate(st0, (d) => {
      for (const n of draftBeat(d, c)!.notes) delete n.tieToNext;
    });
  }
  let st = st0;
  let nref = nextRef(st.score, c);
  if (!nref) {
    st = moveRight(st);
    st = { ...st, cursor: { ...st.cursor, ...c } };
    nref = nextRef(st.score, c);
    if (!nref) return st0;
  }
  const target = nref;
  return mutate(st, (d) => {
    const b = draftBeat(d, c)!;
    const nb = draftBeat(d, target)!;
    for (const n of b.notes) n.tieToNext = true;
    if (nb.rest || nb.notes.length === 0) {
      nb.rest = false;
      nb.notes = b.notes.map((n) => ({ ...createNote(n.pitch as Pitch), string: n.string, fret: n.fret }));
    } else {
      for (const n of b.notes) {
        if (!nb.notes.some((m) => samePitch(m.pitch as Pitch, n.pitch as Pitch))) {
          nb.notes.push({ ...createNote(n.pitch as Pitch), string: n.string, fret: n.fret });
        }
      }
    }
  });
}

/** L: make the cursor beat a tied continuation of the previous beat. */
export function tieToPrevious(st: EditorState): EditorState {
  const c = st.cursor;
  const pref = prevRef(st.score, c);
  if (!pref) return withError(st, 'No previous note to tie from.');
  const prev = voiceOf(st.score, pref)[pref.beat];
  if (!prev || prev.rest || !prev.notes.length) return withError(st, 'The previous beat is a rest.');
  const st1 = ensureBeat(st);
  return mutate(st1, (d) => {
    const pb = draftBeat(d, pref)!;
    const b = draftBeat(d, c)!;
    const allTied = pb.notes.every((n) => n.tieToNext) && !b.rest;
    if (allTied) {
      for (const n of pb.notes) delete n.tieToNext;
      return;
    }
    for (const n of pb.notes) n.tieToNext = true;
    b.rest = false;
    b.notes = pb.notes.map((n) => ({ ...createNote(n.pitch as Pitch), string: n.string, fret: n.fret }));
  });
}

/** S (staff): slur from the first to the last selected beat (or to the next beat). */
export function toggleSlur(st: EditorState): EditorState {
  const refs = selectedRefs(st);
  if (!refs.length) return st;
  const first = refs[0];
  const last = refs.length > 1 ? refs[refs.length - 1] : nextRef(st.score, first);
  if (!last) return withError(st, 'A slur needs a following note.');
  const fb = voiceOf(st.score, first)[first.beat];
  const lb = voiceOf(st.score, last)[last.beat];
  if (!fb || fb.rest || !lb || lb.rest) return withError(st, 'Slurs connect notes, not rests.');
  const on = !topNote(fb).slurStart;
  return mutate(st, (d) => {
    const a = draftBeat(d, first)!;
    const b = draftBeat(d, last)!;
    const ta = a.notes.reduce((x, y) => (midiOf(y.pitch as Pitch) > midiOf(x.pitch as Pitch) ? y : x));
    const tb = b.notes.reduce((x, y) => (midiOf(y.pitch as Pitch) > midiOf(x.pitch as Pitch) ? y : x));
    if (on) {
      ta.slurStart = true;
      tb.slurEnd = true;
    } else {
      delete ta.slurStart;
      delete tb.slurEnd;
    }
  });
}

/** Shift+S: cycle slur side. */
export function cycleSlurSide(st: EditorState): EditorState {
  return mapTargetNotes(st, (n) => {
    if (!n.slurStart) return;
    n.slurSide = n.slurSide === 'above' ? 'below' : n.slurSide === 'below' ? 'auto' : 'above';
  });
}

/** Z: toggle grace (acciaccatura) on the selected beats. */
export function toggleGrace(st: EditorState): EditorState {
  const refs = selectedRefs(st);
  if (!refs.length) return st;
  const on = !voiceOf(st.score, refs[0])[refs[0].beat]?.grace;
  return mutate(st, (d) => {
    for (const r of refs) {
      const b = draftBeat(d, r);
      if (!b) continue;
      if (on) {
        b.grace = 'acciaccatura';
        if (b.duration.base < 8) b.duration = { base: 8, dots: 0 };
      } else delete b.grace;
    }
  });
}

// ---------- generic note/beat property helpers ----------

export function mapTargetNotes(st: EditorState, f: (n: Draft<Note>, beat: Draft<Beat>) => void): EditorState {
  const targets = targetNotes(st);
  if (!targets.length) return st;
  return mutate(st, (d) => {
    for (const { ref, idx } of targets) {
      const b = draftBeat(d, ref)!;
      for (const i of idx) f(b.notes[i], b);
    }
  });
}

export function mapSelectedBeats(st: EditorState, f: (b: Draft<Beat>, ref: BeatRef) => void): EditorState {
  const refs = selectedRefs(st);
  if (!refs.length) return st;
  return mutate(st, (d) => {
    for (const r of refs) {
      const b = draftBeat(d, r);
      if (b) f(b, r);
    }
  });
}

type BoolNoteKey = 'hammerPull' | 'vibrato' | 'dead' | 'tap' | 'leftHandTap' | 'pullOffToNowhere' | 'stringCircle' | 'ghost';

/** Toggle a boolean note flag across the targeted notes (on if any is off). */
export function toggleNoteFlag(st: EditorState, key: BoolNoteKey): EditorState {
  const targets = targetNotes(st);
  if (!targets.length) return withError(st, 'Select a note first.');
  const all = targets.every(({ ref, idx }) => idx.every((i) => voiceOf(st.score, ref)[ref.beat].notes[i][key]));
  return mapTargetNotes(st, (n) => {
    if (all) delete n[key];
    else n[key] = true;
    if (key === 'dead') {
      if (all) delete n.head;
      else n.head = 'x';
    }
  });
}

export function setSlide(st: EditorState, slide: SlideType): EditorState {
  const targets = targetNotes(st);
  if (!targets.length) return withError(st, 'Select a note first.');
  const all = targets.every(({ ref, idx }) => idx.every((i) => voiceOf(st.score, ref)[ref.beat].notes[i].slide === slide));
  return mapTargetNotes(st, (n) => {
    if (all) delete n.slide;
    else n.slide = slide;
  });
}

export function setHarmonic(st: EditorState, kind: 'natural' | 'artificial'): EditorState {
  const targets = targetNotes(st);
  if (!targets.length) return withError(st, 'Select a note first.');
  const all = targets.every(({ ref, idx }) => idx.every((i) => voiceOf(st.score, ref)[ref.beat].notes[i].harmonic === kind));
  return mapTargetNotes(st, (n) => {
    if (all) delete n.harmonic;
    else n.harmonic = kind;
  });
}

export const BEND_PRESETS: Record<string, { label: string; points: { t: number; semis: number }[] }> = {
  full: { label: 'Bend (full)', points: [{ t: 0, semis: 0 }, { t: 0.4, semis: 2 }, { t: 1, semis: 2 }] },
  half: { label: 'Bend (½)', points: [{ t: 0, semis: 0 }, { t: 0.4, semis: 1 }, { t: 1, semis: 1 }] },
  release: { label: 'Bend and release', points: [{ t: 0, semis: 0 }, { t: 0.3, semis: 2 }, { t: 0.6, semis: 2 }, { t: 1, semis: 0 }] },
  pre: { label: 'Pre-bend', points: [{ t: 0, semis: 2 }, { t: 1, semis: 2 }] },
};

export function toggleBend(st: EditorState, preset: keyof typeof BEND_PRESETS = 'full'): EditorState {
  const targets = targetNotes(st);
  if (!targets.length) return withError(st, 'Select a note first.');
  const all = targets.every(({ ref, idx }) => idx.every((i) => voiceOf(st.score, ref)[ref.beat].notes[i].bend));
  return mapTargetNotes(st, (n) => {
    if (all) delete n.bend;
    else n.bend = { points: BEND_PRESETS[preset].points.map((p) => ({ ...p })) };
  });
}

export function setNoteHead(st: EditorState, head: NoteHead): EditorState {
  const targets = targetNotes(st);
  if (!targets.length) return withError(st, 'Select a note first.');
  const all = targets.every(({ ref, idx }) => idx.every((i) => voiceOf(st.score, ref)[ref.beat].notes[i].head === head));
  return mapTargetNotes(st, (n) => {
    if (all) delete n.head;
    else n.head = head;
  });
}

export function setNoteText(st: EditorState, key: 'fingering' | 'rhFingering', value: string): EditorState {
  return mapTargetNotes(st, (n) => {
    if (value) n[key] = value;
    else delete n[key];
  });
}

export function cycleAccidentalDisplay(st: EditorState): EditorState {
  return mapTargetNotes(st, (n) => {
    n.accidentalDisplay = n.accidentalDisplay === 'cautionary' ? 'auto' : 'cautionary';
  });
}

export function toggleNoteHidden(st: EditorState): EditorState {
  return mapTargetNotes(st, (n) => {
    if (n.hidden) delete n.hidden;
    else n.hidden = true;
  });
}

export function setNoteColor(st: EditorState, color: string | null): EditorState {
  return mapTargetNotes(st, (n) => {
    if (color) n.color = color;
    else delete n.color;
  });
}

type BoolBeatKey = 'letRing' | 'palmMute' | 'breath' | 'slap' | 'pop' | 'tap' | 'hidden' | 'small' | 'passage';

export function toggleBeatFlag(st: EditorState, key: BoolBeatKey): EditorState {
  const refs = selectedRefs(st);
  if (!refs.length) return st;
  const all = refs.every((r) => voiceOf(st.score, r)[r.beat]?.[key]);
  return mapSelectedBeats(st, (b) => {
    if (all) delete b[key];
    else b[key] = true;
  });
}

type EnumBeatKey = 'strum' | 'arpeggio' | 'brush' | 'pick' | 'pedal' | 'ornament' | 'jazz' | 'mute' | 'stem';

/** Set (or clear when already set to the same value) an enum-valued beat property. */
export function toggleBeatValue<K extends EnumBeatKey>(st: EditorState, key: K, value: NonNullable<Beat[K]>): EditorState {
  const refs = selectedRefs(st);
  if (!refs.length) return st;
  const all = refs.every((r) => voiceOf(st.score, r)[r.beat]?.[key] === value);
  return mapSelectedBeats(st, (b) => {
    if (all) delete b[key];
    else (b as Record<string, unknown>)[key] = value;
  });
}

export function toggleArticulation(st: EditorState, a: Articulation): EditorState {
  const refs = selectedRefs(st);
  if (!refs.length) return st;
  const all = refs.every((r) => voiceOf(st.score, r)[r.beat]?.articulations?.includes(a));
  return mapSelectedBeats(st, (b) => {
    const list = (b.articulations ?? []).filter((x) => x !== a);
    if (!all) list.push(a);
    if (list.length) b.articulations = list;
    else delete b.articulations;
  });
}

export function setDynamic(st: EditorState, dyn: Dynamic | null): EditorState {
  const c = st.cursor;
  const cur = voiceOf(st.score, c)[c.beat];
  const clear = dyn === null || cur?.dynamics === dyn;
  return mutate(st, (d) => {
    const b = draftBeat(d, c);
    if (!b) return;
    if (clear) delete b.dynamics;
    else b.dynamics = dyn!;
  });
}

/** Hairpin from the first to the last selected beat (or the next beat). */
export function toggleHairpin(st: EditorState, type: 'cresc' | 'dim'): EditorState {
  const refs = selectedRefs(st);
  if (!refs.length) return st;
  const first = refs[0];
  const last = refs.length > 1 ? refs[refs.length - 1] : nextRef(st.score, first) ?? first;
  const fb = voiceOf(st.score, first)[first.beat];
  const lb = voiceOf(st.score, last)[last.beat];
  if (!fb || !lb) return st;
  const off = fb.hairpin?.type === type;
  return mutate(st, (d) => {
    const b = draftBeat(d, first)!;
    if (off) delete b.hairpin;
    else b.hairpin = { type, endBeatId: lb.id };
  });
}

export function setTremolo(st: EditorState): EditorState {
  return mapSelectedBeats(st, (b) => {
    const n = ((b.tremolo ?? 0) + 1) % 4;
    if (n === 0) delete b.tremolo;
    else b.tremolo = n as 1 | 2 | 3;
  });
}

export function setOctaveLine(st: EditorState, shift: 8 | -8 | 15 | -15): EditorState {
  const refs = selectedRefs(st);
  const all = refs.every((r) => voiceOf(st.score, r)[r.beat]?.octaveShift === shift);
  return mapSelectedBeats(st, (b) => {
    if (all) delete b.octaveShift;
    else b.octaveShift = shift;
  });
}

export function setBeatText(st: EditorState, key: 'above' | 'below' | 'innerAbove' | 'innerBelow', value: string): EditorState {
  const c = st.cursor;
  return mutate(st, (d) => {
    const b = draftBeat(d, c);
    if (!b) return;
    const text = { ...(b.text ?? {}) };
    if (value) text[key] = value;
    else delete text[key];
    if (Object.keys(text).length) b.text = text;
    else delete b.text;
  });
}

export function setChord(st: EditorState, chord: ChordSymbol | null): EditorState {
  const c = st.cursor;
  return mutate(st, (d) => {
    const b = draftBeat(d, c);
    if (!b) return;
    if (chord && chord.name) b.chord = chord;
    else delete b.chord;
  });
}

export function setLyric(st: EditorState, verse: number, text: string, ref?: BeatRef): EditorState {
  const r = ref ?? st.cursor;
  return mutate(st, (d) => {
    const b = draftBeat(d, r);
    if (!b) return;
    const l = [...(b.lyrics ?? [])];
    while (l.length <= verse) l.push('');
    l[verse] = text;
    while (l.length && !l[l.length - 1]) l.pop();
    if (l.length) b.lyrics = l;
    else delete b.lyrics;
  });
}

export function setBeatString(st: EditorState, key: 'sticking' | 'roman', value: string): EditorState {
  const c = st.cursor;
  return mutate(st, (d) => {
    const b = draftBeat(d, c);
    if (!b) return;
    if (value) b[key] = value;
    else delete b[key];
  });
}

/** Mod+B: toggle "beam to next" on the selected beats. */
export function toggleBeamToNext(st: EditorState): EditorState {
  const refs = selectedRefs(st);
  const all = refs.every((r) => voiceOf(st.score, r)[r.beat]?.beamBreak === 'join');
  return mapSelectedBeats(st, (b) => {
    b.beamBreak = all ? 'break' : 'join';
  });
}

/** Shift+M: recalculate all beams (clear manual overrides). */
export function rebeamAll(st: EditorState): EditorState {
  return mutate(st, (d) => {
    for (const t of d.tracks) for (const tb of t.bars) for (const v of tb.voices) for (const b of v) delete b.beamBreak;
  });
}

export function flipStem(st: EditorState): EditorState {
  return mapSelectedBeats(st, (b) => {
    b.stem = b.stem === 'up' ? 'down' : b.stem === 'down' ? 'auto' : 'up';
  });
}

export function cycleStemVisibility(st: EditorState): EditorState {
  return mapSelectedBeats(st, (b) => {
    if (b.stem === 'hidden') delete b.stem;
    else b.stem = 'hidden';
  });
}

/** Insert a rest of the input duration before (left) or after (right) the cursor beat. */
export function insertBeat(st: EditorState, side: 'left' | 'right'): EditorState {
  const c = st.cursor;
  const at = side === 'left' ? c.beat : Math.min(c.beat + 1, voiceOf(st.score, c).length);
  const next = mutate(st, (d) => {
    draftVoice(d, c).splice(at, 0, createRest({ ...st.duration }));
  });
  return focusBeat(next, { ...c, beat: at });
}

/** M: repeat the selected beats until the bar is full (or once if it already is). */
export function repeatToEndOfBar(st: EditorState): EditorState {
  const refs = selectedRefs(st).filter((r) => r.bar === st.cursor.bar);
  if (!refs.length) return st;
  const c = st.cursor;
  const cap = barCapacity(timeSigAt(st.score, c.bar));
  const src = refs.map((r) => voiceOf(st.score, r)[r.beat]).filter(Boolean) as Beat[];
  const last = refs[refs.length - 1];
  const next = mutate(st, (d) => {
    const v = draftVoice(d, c);
    let filled = filledTicks(v as Beat[]);
    let at = v.length;
    const copies: Beat[] = [];
    const chunk = src.reduce((t, b) => t + (b.grace ? 0 : ticks(b.duration)), 0);
    if (filled >= cap || chunk === 0) {
      copies.push(...src.map((b) => reId(b)));
      at = last.beat + 1;
    } else {
      while (filled + chunk <= cap) {
        copies.push(...src.map((b) => reId(b)));
        filled += chunk;
      }
    }
    v.splice(at, 0, ...(copies as Draft<Beat>[]));
  });
  return next;
}

/** Fill the remainder of the cursor bar with rests. */
export function fillWithRests(st: EditorState): EditorState {
  const c = st.cursor;
  const cap = barCapacity(timeSigAt(st.score, c.bar));
  const filled = filledTicks(voiceOf(st.score, c));
  if (filled >= cap) return withError(st, 'This bar is already full. Use “Add bar after” to add a new bar.');
  return mutate(st, (d) => {
    let left = cap - filled;
    const v = draftVoice(d, c);
    while (left > 0) {
      const dd = durationForTicks(left) ?? largestFitting(left);
      if (!dd) break;
      v.push(createRest(dd));
      left -= ticks(dd);
    }
  });
}

function largestFitting(t: number): Duration | null {
  for (const base of [1, 2, 4, 8, 16, 32, 64] as DurBase[]) {
    if (ticks({ base, dots: 0 }) <= t) return { base, dots: 0 };
  }
  return null;
}

/** Notes (sounding MIDI) at the cursor beat, for play-on-select. */
export function cursorSoundingMidis(st: EditorState): number[] {
  const c = st.cursor;
  const beat = voiceOf(st.score, c)[c.beat];
  const track = st.score.tracks[c.track];
  if (!beat || beat.rest) return [];
  return beat.notes.map((n) => soundingMidi(n, track));
}

/** Pitch of the staff position under the cursor (for click-to-enter on an empty staff spot). */
export function pitchAtCursorPosition(st: EditorState): Pitch {
  const c = st.cursor;
  const mid = middleLineAt(st.score, c.track, c.bar);
  const key = trackKeyAt(st.score, c.track, c.bar);
  const base = staffPosition(mid, mid);
  return diatonicInterval(mid, Math.abs(c.staffPos - base) + 1, c.staffPos >= 0 ? 1 : -1, key.fifths);
}
